import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Heart } from "lucide-react";
import { Button } from "./ui/button";

interface DonationWidgetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DonationWidget({ isOpen, onClose }: DonationWidgetProps) {
  const [amount, setAmount] = useState<number | null>(500);
  const [frequency, setFrequency] = useState<"once" | "monthly">("once");
  const [customAmount, setCustomAmount] = useState("");

  const presetAmounts = [500, 1000, 2500, 5000];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-primary shadow-2xl"
        >
          <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Heart className="text-primary" size={20} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Make a Donation</h3>
                  <p className="text-sm text-muted-foreground">
                    100% goes directly to projects
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Frequency Toggle */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setFrequency("once")}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                  frequency === "once"
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:border-primary/50"
                }`}
              >
                One-time
              </button>
              <button
                onClick={() => setFrequency("monthly")}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                  frequency === "monthly"
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:border-primary/50"
                }`}
              >
                Monthly
              </button>
            </div>

            {/* Preset Amounts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {presetAmounts.map((preset) => (
                <button
                  key={preset}
                  onClick={() => {
                    setAmount(preset);
                    setCustomAmount("");
                  }}
                  className={`py-4 px-4 rounded-lg border-2 transition-all ${
                    amount === preset && !customAmount
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="text-xl font-semibold">
                    ₹{preset.toLocaleString()}
                  </div>
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="mb-6">
              <input
                type="number"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setAmount(null);
                }}
                placeholder="Enter custom amount"
                className="w-full py-3 px-4 border-2 border-border rounded-lg focus:border-primary focus:outline-none text-lg"
              />
            </div>

            {/* CTA Button */}
            <Button
              size="lg"
              className="w-full text-lg py-6"
              disabled={!amount && !customAmount}
            >
              {frequency === "monthly" ? "Start Monthly Donation" : "Donate Now"}{" "}
              {(amount || customAmount) && (
                <span className="ml-2">
                  ₹{(amount || Number(customAmount)).toLocaleString()}
                </span>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              Secure payment • Tax benefits available • Cancel anytime
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
